/**
 * Text Extraction Utilities
 * Extracts readable text from DOM elements
 */

import { containsMath, speakMathIn } from './mathSpeech';

/**
 * Text of the current selection for reading aloud: like selection.toString(),
 * but math in it (MathML, MathJax, KaTeX) is turned into words.
 */
export function readableSelectionText(sel: Selection | null = window.getSelection()): string {
  const plain = sel?.toString().trim() || '';
  if (!sel || sel.rangeCount === 0 || !plain) return plain;
  try {
    const range = sel.getRangeAt(0);
    const anc = range.commonAncestorContainer;
    const ancEl = anc.nodeType === Node.ELEMENT_NODE ? (anc as Element) : anc.parentElement;
    // Selection inside one formula: read the whole formula
    const formula = ancEl?.closest('math, mjx-container, .katex, .MathJax');
    const holder = document.createElement('div');
    if (formula) holder.appendChild(formula.cloneNode(true));
    else holder.appendChild(range.cloneContents());
    if (!containsMath(holder)) return plain;
    speakMathIn(holder);
    return (holder.textContent || '').replace(/\s+/g, ' ').trim() || plain;
  } catch {
    return plain;
  }
}

/**
 * Never readable, whatever ignoreSelectors a site passes (a custom list replaces
 * the defaults): scripts include JSON-LD structured data, which would otherwise
 * be read aloud as raw JSON. Text inside media/embed elements is fallback for old
 * browsers ("Your browser does not support the video tag.") and never shown.
 */
const ALWAYS_IGNORE = ['script', 'style', 'noscript', 'template', 'video', 'audio', 'canvas', 'iframe', 'object'];

/** Rich text editors / editable regions — a block containing one is not read */
const EDITOR_SELECTOR = '[contenteditable="true"], [contenteditable=""], .tox-tinymce, .mce-tinymce, .ic-RichContentEditor';

/** Text of an iframe's content (frames the widget can read), or null to skip it */
export type FrameTextProvider = (iframe: HTMLIFrameElement) => string | null;

export interface TextExtractorOptions {
  /** Fall back to <body> when no selector matches (default true) */
  fallbackToBody?: boolean;
  /** Document to read (default: this page); a same-origin iframe's document for frames */
  doc?: Document;
  /** Skip blocks inside elements matching this selector (and blocks containing an editor) */
  blockExclude?: string;
}

/**
 * Whether an element is hidden from readers: not rendered (display:none, a
 * hidden ancestor, content-visibility), visibility:hidden, or marked hidden for
 * assistive tech (aria-hidden, inert). Step-by-step forms, tabs and carousels
 * keep their other steps in the DOM this way.
 */
export function isHiddenElement(el: Element): boolean {
  if (el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return true;
  const html = el as HTMLElement;
  if (typeof html.checkVisibility === 'function') {
    return !html.checkVisibility({ visibilityProperty: true } as CheckVisibilityOptions);
  }
  const style = (el.ownerDocument.defaultView || window).getComputedStyle(el);
  return style.display === 'none' || style.visibility === 'hidden';
}

/**
 * Remove from `clone` the elements hidden in `original` (same structure,
 * cloned just before). Hidden subtrees are skipped whole.
 */
function removeHiddenFromClone(original: Element, clone: Element): void {
  if (!original.isConnected) return; // detached: no layout to check
  const hidden: Element[] = [];
  const walk = (orig: Element, copy: Element) => {
    const origKids = orig.children;
    const copyKids = copy.children;
    for (let i = 0; i < origKids.length && i < copyKids.length; i++) {
      if (isHiddenElement(origKids[i])) hidden.push(copyKids[i]);
      else walk(origKids[i], copyKids[i]);
    }
  };
  walk(original, clone);
  hidden.forEach((el) => el.remove());
}

/** Elements that end a run of text */
const BLOCK_SELECTOR = 'p, div, section, article, blockquote, pre, h1, h2, h3, h4, h5, h6, li, dt, dd, td, th, caption, figcaption, tr';
/** Short standalone lines that should be read as their own sentence */
const STANDALONE_SELECTOR = 'h1, h2, h3, h4, h5, h6, li, dt, td, th, caption, figcaption';

export class TextExtractor {
  private lastMatchedElements: Element[] = [];
  private fallbackToBody: boolean;
  private blockExclude: string | undefined;
  private doc: Document;
  /** Reads iframes in place (set by the frame manager); without it iframes are skipped */
  frameText: FrameTextProvider | null = null;

  constructor(
    private selectors: string[],
    private ignoreSelectors: string[],
    options: TextExtractorOptions = {}
  ) {
    this.fallbackToBody = options.fallbackToBody !== false;
    this.blockExclude = options.blockExclude;
    this.doc = options.doc || document;
    this.ignoreSelectors = [...ALWAYS_IGNORE, ...ignoreSelectors.filter((sel) => !ALWAYS_IGNORE.includes(sel))];
  }

  /** Content-only extractors (presets) drop nested duplicates and excluded blocks */
  private get strict(): boolean {
    return !this.fallbackToBody || !!this.blockExclude;
  }

  /**
   * Find content blocks matching the selectors, within `root` (default: document).
   * In strict mode, blocks nested inside another match are dropped and blocks
   * inside / containing editors are skipped.
   */
  findBlocks(root: ParentNode = this.doc): Element[] {
    const found: Element[] = [];
    const seen = new Set<Element>();
    const consider = (el: Element) => {
      if (!seen.has(el)) { seen.add(el); found.push(el); }
    };
    for (const selector of this.selectors) {
      try {
        if (root instanceof Element && root.matches(selector)) consider(root);
        root.querySelectorAll(selector).forEach(consider);
      } catch { /* invalid selector — ignore */ }
    }
    if (!this.strict) return found;
    return found.filter((el) => this.isAllowedBlock(el) && !found.some((o) => o !== el && o.contains(el)));
  }

  private isAllowedBlock(el: Element): boolean {
    if (!this.blockExclude) return true;
    try {
      return !el.closest(this.blockExclude) && !el.querySelector(EDITOR_SELECTOR);
    } catch {
      return true;
    }
  }

  /**
   * Extract text from page based on selectors
   */
  extract(): string {
    const elements = this.findBlocks();

    if (elements.length === 0 && this.fallbackToBody && this.doc.body) {
      elements.push(this.doc.body);
    }

    // Store matched elements for highlighter scoping
    this.lastMatchedElements = elements;
    return this.extractFrom(elements);
  }

  /**
   * Add blocks found after load (dynamic content). Returns the ones that were new.
   * Drops blocks that left the DOM and blocks already covered by a known block.
   */
  addBlocks(blocks: Element[]): Element[] {
    const known = this.lastMatchedElements.filter((el) => el.isConnected && el !== this.doc.body);
    const added: Element[] = [];
    for (const block of blocks) {
      if (!block.isConnected || known.some((k) => k === block || k.contains(block))) continue;
      // A new block that wraps known ones replaces them
      for (let i = known.length - 1; i >= 0; i--) {
        if (block.contains(known[i])) known.splice(i, 1);
      }
      known.push(block);
      added.push(block);
    }
    // Keep document order so playback reads top to bottom
    known.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
    this.lastMatchedElements = known;
    return added;
  }

  /** Text of the currently known blocks */
  extractFrom(elements: Element[] = this.lastMatchedElements): string {
    const texts: string[] = [];
    for (const element of elements) {
      const text = this.extractFromElement(element);
      if (text.trim().length > 0) {
        texts.push(text);
      }
    }
    return texts.join('\n\n');
  }

  /**
   * Get the DOM elements that were matched during the last extract() call.
   * Used to scope word highlighting to the same content areas.
   */
  getMatchedElements(): Element[] {
    return this.lastMatchedElements;
  }

  /**
   * Extract text from a single element
   */
  /** Readable text of one element (same cleaning as page extraction) */
  textOf(element: Element): string {
    return this.extractFromElement(element);
  }

  private extractFromElement(element: Element): string {
    if (element !== this.doc.body && element.isConnected && isHiddenElement(element)) return '';
    // Clone element to avoid modifying DOM, minus anything hidden on the page
    const clone = element.cloneNode(true) as Element;
    // Readable iframes are read in place (before hidden removal: same structure)
    if (this.frameText) this.inlineFrames(element, clone);
    removeHiddenFromClone(element, clone);

    // Math (MathML, MathJax, KaTeX) → spoken words, before anything is removed
    if (containsMath(clone)) speakMathIn(clone);

    // Remove ignored elements
    for (const selector of this.ignoreSelectors) {
      const ignored = clone.querySelectorAll(selector);
      ignored.forEach(el => el.remove());
    }

    // Publisher pronunciation hints: <span data-rv-say="Reading Vox">ReadingVox</span>
    clone.querySelectorAll('[data-rv-say]').forEach(el => {
      el.textContent = el.getAttribute('data-rv-say') || el.textContent;
    });

    // textContent glues block elements together ("Photosynthesis readingPlants make…")
    // and a heading without punctuation runs into the next sentence. Insert breaks:
    // a sentence end after headings / list items / cells that lack one, a space after other blocks.
    clone.querySelectorAll(BLOCK_SELECTOR).forEach(el => {
      const own = (el.textContent || '').trim();
      if (!own) return;
      const needsStop = el.matches(STANDALONE_SELECTOR) && !/[.!?:;]["')\]]*$/.test(own);
      el.appendChild(document.createTextNode(needsStop ? '. ' : ' '));
    });
    clone.querySelectorAll('br').forEach(br => br.replaceWith(document.createTextNode(' ')));

    // Get text content
    const text = clone.textContent || '';

    // Clean up whitespace
    return text
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n\n')
      .trim();
  }

  /**
   * Replace each readable iframe in `clone` with its content's text. Iframe
   * elements have no element children, so swapping one for a <div> keeps the
   * clone's structure in step with the original for the hidden-element pass.
   */
  private inlineFrames(original: Element, clone: Element): void {
    const origFrames = Array.from(original.querySelectorAll('iframe'));
    if (original instanceof HTMLIFrameElement) origFrames.unshift(original);
    if (origFrames.length === 0) return;
    const cloneFrames = Array.from(clone.querySelectorAll('iframe'));
    if (clone instanceof HTMLIFrameElement) cloneFrames.unshift(clone);
    origFrames.forEach((frame, i) => {
      const copy = cloneFrames[i];
      if (!copy || !copy.parentNode) return;
      // An iframe the site marked to skip (e.g. class="no-read") stays skipped
      // (the built-in rules only drop iframe *fallback* text, so they don't count here)
      if (this.ignoreSelectors.some((sel) => {
        if (ALWAYS_IGNORE.includes(sel)) return false;
        try { return frame.matches(sel); } catch { return false; }
      })) return;
      let text: string | null = null;
      try { text = this.frameText!(frame); } catch { text = null; }
      if (!text || !text.trim()) return;
      const holder = this.doc.createElement('div');
      holder.textContent = text;
      copy.parentNode.replaceChild(holder, copy);
    });
  }

  /**
   * Extract text from selection
   */
  static extractFromSelection(): string | null {
    const selection = window.getSelection();

    if (!selection || selection.toString().trim().length === 0) {
      return null;
    }

    return selection.toString().trim();
  }

  /**
   * Get selection range
   */
  static getSelectionRange(): { start: number; end: number; text: string } | null {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      return null;
    }

    const range = selection.getRangeAt(0);
    const text = selection.toString().trim();

    if (text.length === 0) {
      return null;
    }

    // Get character offsets
    const preRange = range.cloneRange();
    preRange.selectNodeContents(document.body);
    preRange.setEnd(range.startContainer, range.startOffset);

    const start = preRange.toString().length;
    const end = start + text.length;

    return { start, end, text };
  }
}
